/** Pickers grounded in the catalog: columns of the base transaction (with the
    facts that decide whether a wizard may write them), steps, objects, actions,
    and the key → template maps nearly every block is made of. */

import type React from 'react'
import type { CatalogColumn, CatalogForm } from '@/api/wizards'
import ExprInput from './ExprInput'
import TypedValueInput from './TypedValueInput'
import {
  columnOfTarget, headerForm, lineFormFor, lineForms, useWizardDesign,
} from './context'
import type { TokenGroup } from './context'
import {
  AddBtn, Badge, RemoveBtn, SelectInput, TextInput, emptyHintStyle, hintStyle,
} from './ui'

/** Mandatory / protected / item_change, as badges. */
export function ColumnBadges({ col }: { col: CatalogColumn | null }) {
  if (!col) return null
  return (
    <span style={{ display: 'inline-flex', gap: 3, flexWrap: 'wrap' }}>
      {col.mandatory && <Badge tone="accent" title="Mandatory in the transaction">required</Badge>}
      {col.protect && <Badge tone="error" title="Protected — a wizard cannot set it; set the column that drives it">protected</Badge>}
      {col.item_change && (
        <Badge tone="warn" title={`Setting it runs its item change (${col.item_change})${col.fills?.length ? ` — fills ${col.fills.join(', ')}` : ''}`}>
          item change{col.item_change !== 'rule' ? `: ${col.item_change}` : ''}
        </Badge>
      )}
    </span>
  )
}

function colLabel(c: CatalogColumn): string {
  const flags = [
    c.mandatory ? '*' : '',
    c.protect ? ' (protected)' : '',
    c.item_change ? ' ⚡' : '',
  ].join('')
  return `${c.name}${c.label && c.label !== c.name ? ` — ${c.label}` : ''}${flags}`
}

/**
 * A wizard TARGET: `header.COL`, `line.COL` (the step's line form) or
 * `var.NAME`. Protected columns are listed but disabled when `writable` —
 * the validator refuses them, so offering them would only invite an error.
 */
export function TargetPicker({
  value, onChange, formNo, writable = true, allowVars = true, scopes = ['header', 'line'],
}: {
  value: string | undefined
  onChange: (v: string) => void
  formNo?: string
  writable?: boolean
  allowVars?: boolean
  scopes?: Array<'header' | 'line'>
}) {
  const ctx = useWizardDesign()
  const header = headerForm(ctx.base)
  const line = lineFormFor(ctx.base, ctx.model, formNo)
  const v = value || ''
  const col = columnOfTarget(ctx, v, formNo)
  const groups: Array<{ label: string; options: Array<{ value: string; label: string; disabled?: boolean }> }> = []
  if (scopes.includes('header') && header) {
    groups.push({
      label: `Header (form ${header.form_no})`,
      options: header.columns.map((c) => ({
        value: `header.${c.name}`, label: colLabel(c), disabled: writable && c.protect,
      })),
    })
  }
  if (scopes.includes('line') && line) {
    groups.push({
      label: `Current line (form ${line.form_no})`,
      options: line.columns.map((c) => ({
        value: `line.${c.name}`, label: colLabel(c), disabled: writable && c.protect,
      })),
    })
  }
  if (allowVars && (ctx.model.vars || []).length) {
    groups.push({
      label: 'Variables',
      options: (ctx.model.vars || []).map((x) => ({ value: `var.${x.name}`, label: `var.${x.name}` })),
    })
  }
  const known = v === '' || groups.some((g) => g.options.some((o) => o.value === v))
  return (
    <div>
      <div style={{ display: 'flex', gap: 6 }}>
        <select value={known ? v : '__other'} disabled={ctx.readOnly}
          onChange={(e) => { if (e.target.value !== '__other') onChange(e.target.value) }}
          style={{ ...pickerSelect, flex: 1 }}>
          <option value="">— pick a column —</option>
          {groups.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.options.map((o) => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
            </optgroup>
          ))}
          {!known && <option value="__other">{v}</option>}
        </select>
        {!known && (
          <TextInput value={v} onChange={onChange} mono style={{ flex: 1 }} />
        )}
      </div>
      {col && <div style={{ marginTop: 3 }}><ColumnBadges col={col} /></div>}
      {!ctx.base && <div style={hintStyle}>Pick the base transaction first (Base &amp; Mode).</div>}
    </div>
  )
}

const pickerSelect: React.CSSProperties = {
  width: '100%', padding: '5px 7px', fontSize: 14, border: '1px solid var(--color-border)',
  borderRadius: 4, background: 'var(--color-bg-secondary)', color: 'var(--color-text)',
}

/** Several columns of ONE form, as toggle chips. */
export function ColumnMulti({
  form, values, onChange, prefix = '', emptyText,
}: {
  form: CatalogForm | null
  values: string[] | undefined
  onChange: (v: string[]) => void
  /** Stored with a prefix (`line.` for also_writes) or bare (grid columns). */
  prefix?: string
  emptyText?: string
}) {
  const ctx = useWizardDesign()
  const list = values || []
  const set = new Set(list.map((x) => x.toLowerCase()))
  if (!form) return <div style={emptyHintStyle}>{emptyText || 'No form to pick columns from.'}</div>
  const orphans = list.filter((x) => {
    const bare = prefix && x.toLowerCase().startsWith(prefix.toLowerCase()) ? x.slice(prefix.length) : x
    return !form.columns.some((c) => c.name.toLowerCase() === bare.toLowerCase())
  })
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {form.columns.map((c) => {
        const key = `${prefix}${c.name}`
        const on = set.has(key.toLowerCase())
        return (
          <button key={c.name} type="button" disabled={ctx.readOnly}
            title={[c.label, c.mandatory ? 'required' : '', c.protect ? 'protected' : '',
              c.item_change ? `item change (${c.item_change})` : ''].filter(Boolean).join(' · ')}
            onClick={() => onChange(on ? list.filter((x) => x.toLowerCase() !== key.toLowerCase()) : [...list, key])}
            style={{
              fontSize: 13, padding: '2px 8px', borderRadius: 10, cursor: 'pointer',
              border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-border)'}`,
              background: on ? 'var(--color-accent)' : 'var(--color-bg)',
              color: on ? '#fff' : (c.protect ? 'var(--color-text-secondary)' : 'var(--color-text)'),
            }}>
            {c.name}{c.mandatory ? '*' : ''}{c.item_change ? ' ⚡' : ''}
          </button>
        )
      })}
      {orphans.map((o) => (
        <button key={o} type="button" title="Not a column of this form — click to remove"
          onClick={() => onChange(list.filter((x) => x !== o))}
          style={{
            fontSize: 13, padding: '2px 8px', borderRadius: 10, cursor: 'pointer',
            border: '1px dashed var(--color-error, #d33)', background: 'transparent',
            color: 'var(--color-error, #d33)',
          }}>
          {o} ✕
        </button>
      ))}
    </div>
  )
}

/** A line form picker (form_no). */
export function LineFormPicker({ value, onChange, empty = 'Wizard line form' }: {
  value: string | undefined; onChange: (v: string) => void; empty?: string
}) {
  const ctx = useWizardDesign()
  return (
    <SelectInput value={value} onChange={onChange} empty={empty} disabled={ctx.readOnly}
      options={lineForms(ctx.base).map((f) => ({
        value: f.form_no, label: `Form ${f.form_no}${f.title ? ` — ${f.title}` : ''}`,
      }))} />
  )
}

/** A step id (goto / resume / body …). */
export function StepPicker({ value, onChange, empty = '—', extra = [], exclude }: {
  value: string | undefined
  onChange: (v: string) => void
  empty?: string
  extra?: Array<{ value: string; label: string }>
  exclude?: string
}) {
  const ctx = useWizardDesign()
  return (
    <SelectInput value={value} onChange={onChange} empty={empty} disabled={ctx.readOnly}
      options={[...extra, ...ctx.model.steps.filter((s) => s.id !== exclude).map((s) => ({
        value: s.id, label: `${s.id}${s.title ? ` — ${s.title}` : ''} (${s.kind})`,
      }))]} />
  )
}

/** A `goto:<step>` / other route string (on_error, on_none, pick_row.then). */
export function RouteStringPicker({ value, onChange, options }: {
  value: string | undefined
  onChange: (v: string) => void
  /** Fixed routes offered beside `goto:<step>` (e.g. `add_line`). */
  options?: Array<{ value: string; label: string }>
}) {
  const ctx = useWizardDesign()
  const v = value || ''
  const isError = v.startsWith('error:')
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <select value={isError ? '__error' : v} disabled={ctx.readOnly}
        onChange={(e) => onChange(e.target.value === '__error' ? 'error:' : e.target.value)}
        style={{ ...pickerSelect, flex: 1 }}>
        <option value="">—</option>
        {(options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        {ctx.model.steps.map((s) => <option key={s.id} value={`goto:${s.id}`}>go to {s.id}</option>)}
        <option value="__error">stop with a message…</option>
        {v && !isError && !v.startsWith('goto:') && !(options || []).some((o) => o.value === v) && (
          <option value={v}>{v}</option>
        )}
      </select>
      {isError && (
        <TextInput value={v.slice('error:'.length)} onChange={(t) => onChange(`error:${t}`)}
          placeholder="Message shown" style={{ flex: 1 }} />
      )}
    </div>
  )
}

export type ObjectKind = 'T' | 'V' | 'D' | 'R' | 'U' | 'G' | 'K'

/** An object of one type from the catalog (free text for types it does not
 *  enumerate, e.g. G). */
export function ObjectPicker({ objType, value, onChange }: {
  objType: ObjectKind
  value: string | undefined
  onChange: (v: string) => void
}) {
  const ctx = useWizardDesign()
  const cat = ctx.catalog
  const list: Array<{ obj_name: string; title?: string }> = !cat ? []
    : objType === 'T' ? cat.transactions
      : objType === 'V' ? cat.visuals
        : objType === 'D' ? cat.dashboards
          : objType === 'R' ? cat.reports
            : objType === 'U' ? cat.wizards.filter((w) => w.obj_name !== ctx.model.obj_name)
              : objType === 'K' ? cat.repositories
                : []
  if (!list.length && objType === 'G') {
    return <TextInput value={value} onChange={onChange} placeholder="Process name" />
  }
  return (
    <SelectInput value={value} onChange={onChange} empty="— pick —" disabled={ctx.readOnly}
      options={list.map((o) => ({ value: o.obj_name, label: o.title ? `${o.obj_name} — ${o.title}` : o.obj_name }))} />
  )
}

/** Criteria / prompt NAMES of a V / D / R (null when unknown). */
export function useParamNames(objType: ObjectKind, objName: string | undefined): string[] | null {
  const ctx = useWizardDesign()
  if (!ctx.catalog || !objName) return null
  const list = objType === 'V' ? ctx.catalog.visuals
    : objType === 'D' ? ctx.catalog.dashboards
      : objType === 'R' ? ctx.catalog.reports : null
  if (!list) return null
  return list.find((o) => o.obj_name === objName)?.params ?? null
}

/** A base-T action button (filtered by handler: `system` is never allowed,
 *  design §7.4). */
export function ActionPicker({ value, onChange }: { value: string | undefined; onChange: (v: string) => void }) {
  const ctx = useWizardDesign()
  const actions = ctx.base?.actions || []
  return (
    <SelectInput value={value} onChange={onChange} empty="— pick an action —" disabled={ctx.readOnly}
      options={actions.map((a) => ({
        value: a.name,
        label: `${a.label || a.name}${a.handler ? ` (${a.handler})` : ''}${a.handler === 'system' ? ' — not allowed in a wizard' : ''}`,
        disabled: a.handler === 'system',
      }))} />
  )
}

/**
 * A key → value map (`params`, `with`, `set`, `map`, `match` …). Keys come
 * from `keyOptions` when the catalog knows them (criteria names, a child's
 * inputs, columns) and can still be typed; values are templates (or plain
 * text / JSONPath when `valueMode` says so). A template value that is a
 * literal keeps its JSON type (TypedValueInput / literals.ts).
 */
export function KeyValueEditor({
  value, onChange, keyOptions, keyPlaceholder = 'name', valuePlaceholder = 'value',
  valueMode = 'template', formNo, extraTokens, keyRender, requiredKeys, addLabel = 'Add',
}: {
  value: Record<string, unknown> | undefined
  onChange: (v: Record<string, unknown>) => void
  keyOptions?: Array<{ value: string; label: string }> | string[]
  keyPlaceholder?: string
  valuePlaceholder?: string
  valueMode?: 'template' | 'expr' | 'text'
  formNo?: string
  extraTokens?: TokenGroup[]
  /** Replaces the key cell (a TargetPicker for `map` / `set`). */
  keyRender?: (key: string, setKey: (k: string) => void) => React.ReactNode
  /** Keys that must be given — flagged when missing. */
  requiredKeys?: string[]
  addLabel?: string
}) {
  const ctx = useWizardDesign()
  const entries = Object.entries(value || {})
  const opts = (keyOptions || []).map((o) => (typeof o === 'string' ? { value: o, label: o } : o))
  const setEntries = (next: Array<[string, unknown]>) => {
    const out: Record<string, unknown> = {}
    for (const [k, v] of next) out[k] = v
    onChange(out)
  }
  const missing = (requiredKeys || []).filter((k) => !entries.some(([key]) => key === k))
  return (
    <div>
      {entries.length === 0 && <div style={emptyHintStyle}>None.</div>}
      {entries.map(([k, v], i) => (
        <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: 4 }}>
          <div style={{ flex: '0 0 38%', minWidth: 0 }}>
            {keyRender ? keyRender(k, (nk) => setEntries(entries.map((e, j) => (j === i ? [nk, e[1]] : e))))
              : opts.length ? (
                <SelectInput value={k} disabled={ctx.readOnly}
                  onChange={(nk) => setEntries(entries.map((e, j) => (j === i ? [nk, e[1]] : e)))}
                  options={opts} empty="—" />
              ) : (
                <TextInput value={k} mono placeholder={keyPlaceholder}
                  onChange={(nk) => setEntries(entries.map((e, j) => (j === i ? [nk, e[1]] : e)))} />
              )}
            {requiredKeys?.includes(k) && <Badge tone="accent">required</Badge>}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {valueMode === 'text' ? (
              <TextInput value={v === undefined || v === null ? '' : String(v)} mono placeholder={valuePlaceholder}
                onChange={(nv) => setEntries(entries.map((e, j) => (j === i ? [e[0], nv] : e)))} />
            ) : valueMode === 'template' ? (
              // A literal keeps its JSON type (5, true, null), never "5".
              <TypedValueInput value={v} placeholder={valuePlaceholder} formNo={formNo}
                extraTokens={extraTokens}
                onChange={(nv) => setEntries(entries.map((e, j) => (j === i ? [e[0], nv] : e)))} />
            ) : (
              <ExprInput value={v} mode={valueMode} placeholder={valuePlaceholder} formNo={formNo}
                extraTokens={extraTokens}
                onChange={(nv) => setEntries(entries.map((e, j) => (j === i ? [e[0], nv] : e)))} />
            )}
          </div>
          {!ctx.readOnly && <RemoveBtn onClick={() => setEntries(entries.filter((_, j) => j !== i))} />}
        </div>
      ))}
      {missing.length > 0 && (
        <div style={{ ...hintStyle, color: 'var(--color-error, #d33)' }}>
          Required and not supplied: {missing.join(', ')}
        </div>
      )}
      {!ctx.readOnly && (
        <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
          <AddBtn onClick={() => {
            const firstFree = opts.find((o) => !entries.some(([k]) => k === o.value))
            // Keys are unique: a second blank row would overwrite the first.
            const key = firstFree?.value
              ?? (entries.some(([k]) => k === '') ? `${keyPlaceholder}_${entries.length + 1}` : '')
            setEntries([...entries, [key, '']])
          }}>{addLabel}</AddBtn>
          {missing.length > 0 && (
            <AddBtn onClick={() => setEntries([...entries, ...missing.map((k) => [k, ''] as [string, unknown])])}>
              Add the required ones
            </AddBtn>
          )}
        </div>
      )}
    </div>
  )
}
